import _ from 'lodash';
import moment from 'moment';

export const nameRegex = /^[a-zA-Z\-\,\.\' ]{1,30}$/;
export const phoneRegex = /^\(?([0-9]{3})\)?[- ]?([0-9]{3})[- ]?([0-9]{4})$/;
export const emailRegex = /^([a-zA-Z0-9_\-\.]{0,}([a-zA-Z0-9_\-]){1})@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9\-]+\.)+))([a-zA-Z]{2,3}|com|org|net|gov|mil|biz|info|mobi|name|aero|jobs|museum|[0-9]{1,3})(\]?)$/;
export const ssnRegex = /^\(?([0-9]{3})\)?[- ]?([0-9]{2})[- ]?([0-9]{4})$/;
export const cvvRegex = /^[0-9]{3,4}$/;
export const creditCardRegex = /^\d{4} *\d{4} *\d{4} *\d{1,4}$/;
export const expirationMonthRegex = /^[0-9]{2}$/;
export const expirationYearRegex = /^[0-9]{4}$/;
export const streetAddressRegex = /^.{1,50}$/;
export const unitNumberRegex = /^.{0,10}$/;
export const cityRegex = /^[a-zA-Z][a-zA-Z ]*$/;
export const stateRegex = /[a-zA-Z]{2}$/;
export const zipCodeRegex = /^\d{5}$/;
export const numRegex = /^\d{9}$/;

const isEmpty = value => value === undefined || value === null || value === '';
const join = (rules) => (value, data) => rules.map(rule => rule(value, data)).filter(error => !!error)[0 /* first error */ ];

export const isConditionalFieldMatching = (data, fieldGroups = [[]]) => {
  return fieldGroups.some(fieldGroup => {
    return fieldGroup.every(field => {
      if (!isEmpty(field.value) && !isEmpty(field.name) && field.value === _.get(data, field.name)) {
        return true;
      }
    });
  });
};

export const validateCCExpirationDate = (errorMsg = 'Please enter a valid value', fieldGroups) => {

 return (value, data) => {
   value = value || '';
   const expirationMonth =parseInt(_.get(data, "creditCardDetails.expirationMonth"));
   const currentMonth = parseInt(data["currentMonth"]);
   const expirationYear = parseInt(_.get(data, "creditCardDetails.expirationYear"));
   const currentYear = parseInt(data["currentYear"]);

   if (!expirationYear || !currentMonth || !currentYear) {
     return;
   }

   if (isConditionalFieldMatching(data, fieldGroups) &&
     expirationMonth < currentMonth &&
     expirationYear <= currentYear) {
     return errorMsg;
   }
 };
};

export const email = (errorMsg = 'Invalid email address') => {
  return value => {
    if (!isEmpty(value) && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value)) {
      return errorMsg;
    }
  };
};

export const required = (errorMsg = 'Required', fieldGroups) => {
  return (value, data) => {
    if (isConditionalFieldMatching(data, fieldGroups) && isEmpty(value)) {
      return errorMsg;
    }
  };
};

export const minLength = (min) => {
  return value => {
    if (!isEmpty(value) && value.length < min) {
      return `Must be at least ${min} characters`;
    }
  };
};

// conditionalFields us used when validation
// is conditional based on the value of other fields
export const match = (regex, errorMsg = 'Please enter a valid value', fieldGroups) => {
  return (value, data) => {
    value = value || '';
    if (isConditionalFieldMatching(data, fieldGroups) && !regex.test(value)) {
      return errorMsg;
    }
  };
};

export const matchOrBlank = (regex, errorMsg = 'Please enter a valid value', fieldGroups) => {
  return (value, data) => {
    if (!!value && isConditionalFieldMatching(data, fieldGroups) && !regex.test(value)) {
      return errorMsg;
    }
  };
};

export const equal = (valueToMatch, errorMsg = 'Please enter a valid value', fieldGroups) => {
  return (value, data) => {
    value = value || '';
    if (isConditionalFieldMatching(data, fieldGroups) && valueToMatch !== value) {
      return errorMsg;
    }
  };
};

export const meetsCondition = (check, errorMsg = 'Please enter a valid value', fieldGroups) => {
  return (value, data) => {
    if (isConditionalFieldMatching(data, fieldGroups) && check(value) !== true) {
      return errorMsg;
    }
  };
};

export const maxLength = (max) => {
  return value => {
    if (!isEmpty(value) && value.length > max) {
      return `Must be no more than ${max} characters`;
    }
  };
};

export const integer = (value) => {
  if (!Number.isInteger(Number(value))) {
    return 'Must be an integer';
  }
};

export const oneOf = (enumeration) => {
  return value => {
    if (!~enumeration.indexOf(value)) {
      return `Must be one of: ${enumeration.join(', ')}`;
    }
  };
};

export const equalsFieldValue = (field) => {
  return (value, data) => {
    if (data) {
      if (value !== data[field]) {
        return 'Do not match';
      }
    }
  };
};

export const validDate = (errorMsg = 'Please enter a valid date') => {
  return (value) => {
    const compareMoment = moment(value);

    if (!compareMoment.isValid()) {
      return errorMsg;
    }
  };
};

export const validateDateRange = (errorMsg = 'The selected time') => {
  return (value, data) => {
    const compareMoment = moment(value);
    const startDate = _.get(data, "startDate");
    const endDate = _.get(data, "endDate");
    const startDateFormatted = moment(startDate).format('M/DD/YYYY');
    const endDateFormatted = moment(endDate).format('M/DD/YYYY');

    if (!compareMoment.isBetween(startDateFormatted, endDateFormatted, null, '[]')) {
      return `${errorMsg} needs to be after ${startDateFormatted} and before ${endDateFormatted}`;
    }
  };
};

export const afterDisconnectDate = (errorMsg = 'Please enter a valid date') => {
  return (value, data) => {
    const compareMoment = moment(value);
    const serviceStopDate = _.get(data, "serviceStopDate");
    const serviceStopDateFormatted = moment(serviceStopDate).format('M/DD/YYYY');

    if (compareMoment.isBefore(serviceStopDateFormatted)) {
      return errorMsg;
    }
  };
};

export const createValidator = (rules) => {
  return (data = {}) => {
    const errors = {};

    Object.keys(rules).forEach((key) => {
      const rule = join([].concat(rules[key])); // concat enables both functions and arrays of functions
      const error = rule(_.get(data, key), data);

      if (error) {
        _.set(errors, key, error);
      }
    });
    // USE FOR TROUBLESHOOTING
    // EXPOSED IN PROPS.ERROR IN DECORATED CONTAINERS
    //  _.set(errors, '_error', errors);
    return errors;
  };
};



/** WEBPACK FOOTER **
 ** ./client/util/form/validation.js
 **/